import { deflate, ungzip } from 'pako';

import { BaseClient } from '../BaseClient';
import { b64toBlob, getMimeType } from '../Common';
import { Empty, String$ } from '../compiled-protos/common';
import { BucketObject, CompressConfig, CompressMoveConfig, File as ProtoFile, FileChunk, FileIdList, FileObject, type FileObjects, InternalResource, InternalResourceList, MoveConfig, MultipartData, MultipartDetails, MultipartUpload, URLObject } from '../compiled-protos/s3';
import { S3ServiceClient } from '../compiled-protos/s3.client';

/**
 * File Client
 */
class S3Client extends BaseClient {
  self: S3ServiceClient;
  constructor(host: string, userID?: number) {
    super(host, userID);
    this.self = new S3ServiceClient(this.transport);
  }

  /**
   * ListFiles returns all files in a bucket.
   * This is not scalable as this array is going to be huge.
   * The aim is to get all files in a bucket that are associated with some paramater.
   * Currenly I am listing every file in the bucket and filtering them out.
   * This function is conceived for use with the K-Copilot module and the kalos-ai-assistant-files bucket.
   * Files in that bucket are named so as to represent a folder structure deliniated by a /
   * e.g. walmart/194019/changemanagement/ELE Core Locations.pdf
   * @param bucket - the name of the bucket
   * @param prefix - an optional prefix of the file name e.g. walmart/194019/
   * @returns an array of FileObjects, filtered by prefix if so provided
   */
  public async ListFiles(bucket: string, prefix?: string): Promise<FileObject[]> {
    const req = BucketObject.create({ name: bucket });
    let res: FileObjects;

    try {
      res = await this.self.getBucket(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return [];
    }
    console.log('prefix', prefix);
    if (!prefix) return res.result;

    const filteredFiles = res.result.filter(file => file.key.startsWith(prefix));
    return filteredFiles;
  }


  // Function to check if the data is gzipped
  public isGzipped = (data: Uint8Array): boolean => {
    // Gzip files start with the bytes 1f 8b
    return data[0] === 0x1f && data[1] === 0x8b;
  };

  public async Get(req: FileObject) {
    const data = await this.self.get(req, this.getMetaData());

    console.log('Retrieved data:', data);

    if (data.response && data.response.data) {
      const responseData = new Uint8Array(data.response.data);

      // Check if the data is gzipped
      if (this.isGzipped(responseData)) {
        try {
          const unzipped = ungzip(responseData);
          data.response.data = unzipped;
          console.log('Unzipped data successfully');
        } catch (err) {
          console.error('Error unzipping data:', err);
        }
      } else {
        console.log('Data is not gzipped, proceeding without unzipping');
      }
    }

    return data;
  }


  public async Move(
    source: SimpleFile,
    destination: SimpleFile,
    preserveSource = false
  ) {
    const req = MoveConfig.create({
      source: FileObject.create({ ...source }),
      destination: FileObject.create({ ...destination }),
      preserveSource,
    });
    let res = Empty.create();
    try {
      res = await this.self.move(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async Create(req: FileObject, withMessage = false) {
    const data = req.data;
    try {
      req.data = deflate(data);
    } catch (err) {
      console.log('failed to deflate filedata', err);
      console.log('filedata', data);
    }
    let res = FileObject.create();
    try {
      res = await this.self.create(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async Delete(req: FileObject) {
    let res = FileObject.create();
    try {
      res = await this.self.delete(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetUploadURL(req: URLObject) {
    let res = URLObject.create();
    try {
      res = await this.self.getUploadURL(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public Client_GetDownloadURL({ authToken, req }: { authToken: string, req: URLObject }) {
    return this.self.getDownloadURL(req, this.getMetaData(authToken)).response;
  }

  public async GetDownloadURL(req: URLObject) {
    let res = URLObject.create();
    try {
      res = await this.self.getDownloadURL(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }
  public async GetExpireUri(req: string) {
    let res = URLObject.create();
    try {
      res = await this.self.getExpiringUri(String$.create({ value: req }), this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;

  }
  public async download(fileName: string, bucket: string, doInflate = false) {
    const req = FileObject.create({ bucket: bucket, key: fileName });
    const res = await this.Get(req);
    const data = res.response.data;
    const el = document.createElement('a');
    const fd = new Blob([data as BlobPart], { type: getMimeType(fileName) });
    el.href = URL.createObjectURL(fd);
    el.download = fileName;
    el.click();
    el.remove();
  }

  public async downloadFile({ bucket, fileName }: { bucket: string, fileName: string }): Promise<File> {
    const urlObj = URLObject.create();
    urlObj.bucket = bucket;
    urlObj.key = fileName;
    const urlRes = await this.self.getDownloadURL(urlObj);
    const downloadRes = await fetch(urlRes.response.url);

    if (downloadRes.status !== 200) throw new Error('Failed to download file');
    const blob = await downloadRes.blob();
    const file = new File([blob], fileName, { type: blob.type });

    return file;
  }


  public async downloadAlt(fileName: string, bucket: string) {
    const req = URLObject.create({ bucket: bucket, key: fileName });
    const res = await this.GetDownloadURL(req);
    const el = document.createElement('a');
    el.download = fileName;
    el.href = res!.url;
    el.click();
    el.remove();
  }


  public b64toBlob(b64Data: string, fileName: string) {
    const sliceSize = 512;
    const byteCharacters = atob(b64Data);
    const byteArrays: Uint8Array[] = [];
    const contentType = getMimeType(fileName);

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays as BlobPart[], { type: contentType });
    return blob;
  }

  public getFileS3BucketUrl = async (filename: string, bucket: string) => {
    const url = URLObject.create({ bucket: bucket, key: filename });
    const dlURL = await this.GetDownloadURL(url);
    return dlURL?.url || "";
  };

  public deleteFileFromS3Buckets = async (key: string, bucket: string) => {
    const req = FileObject.create({ key, bucket });
    return await this.Delete(req);
  };

  public moveFileBetweenS3Buckets = async (
    from: SimpleFile,
    to: SimpleFile,
    preserveSource = false
  ) => {
    try {
      const res = await this.Move(from, to, preserveSource);
      return res ? 'ok' : 'nok';
    } catch (e) {
      return 'nok';
    }
  };

  public openFile = async (filename: string, bucket: string) => {
    const url = await this.getFileS3BucketUrl(filename, bucket);
    window.open(url, '_blank');
  };

  public uploadFileToS3Bucket = async (
    fileName: string,
    fileData: string,
    bucketName: string,
    tagString?: string
  ) => {
    try {
      const urlObj = URLObject.create({
        key: fileName,
        bucket: bucketName,
        tagString,
      });
      urlObj.contentType = getMimeType(fileName) || '';
      const urlRes = await this.GetUploadURL(urlObj);
      console.log('urlRes', urlRes);
      const uploadRes = await fetch(urlRes!.url, {
        body: b64toBlob(fileData.split(';base64,')[1], fileName),
        method: 'PUT',
        headers: tagString
          ? {
            'x-amz-tagging': tagString,
          }
          : {},
      });
      console.log('uploadRes', uploadRes);
      if (uploadRes.status === 200) {
        return 'ok';
      }
      return 'nok';
    } catch (e) {
      return 'nok';
    }
  };

  public async BatchGetInternalResource(req: InternalResource): Promise<InternalResourceList> {
    return await this.self.batchGetInternalResource(req, this.getMetaData()).response;
  }

  public loadInternalResources = async ({
    page,
    filter,
    sort,
  }: LoadInternalResource): Promise<InternalResourceList> => {
    const req = InternalResource.create();
    req.pageNumber = page;

    // Apply filters
    if (filter.tag) {
      req.tag = filter.tag.toString();
    }
    if (filter.name) {
      req.name = `%${filter.name}%`;
    }
    if (filter.description) {
      req.description = `%${filter.description}%`;
    }

    // Apply sorting
    req.orderBy = sort.orderBy;
    req.orderDir = sort.orderDir;

    return await this.BatchGetInternalResource(req);
  };

  public async Compress(req: CompressConfig) {
    let res = FileObject.create();
    try {
      res = await this.self.compress(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async CompressAndUpload(req: CompressConfig) {
    let res = FileObject.create();
    try {
      res = await this.self.compressAndUpload(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async CompressAndMove(req: CompressMoveConfig) {
    let res = Empty.create();
    try {
      res = await this.self.compressAndMove(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Helper method for compressing and moving files
  public compressAndMoveFile = async (
    source: SimpleFile,
    destination: SimpleFile,
    compressionOptions: CompressionOptions,
    preserveSource = false
  ) => {
    try {
      const compressConfig = CompressConfig.create({
        source: FileObject.create({ ...source }),
        maxWidth: compressionOptions.maxWidth,
        maxHeight: compressionOptions.maxHeight,
        quality: compressionOptions.quality,
        format: compressionOptions.format,
      });

      const req = CompressMoveConfig.create({
        compress: compressConfig,
        destination: FileObject.create({ ...destination }),
        preserveSource,
      });

      const res = await this.CompressAndMove(req);
      return res ? 'ok' : 'nok';
    } catch (e) {
      console.error('Error in compressAndMoveFile:', e);
      return 'nok';
    }
  };

  // Helper method for compressing an image file
  public compressImage = async (
    source: SimpleFile,
    compressionOptions: CompressionOptions
  ) => {
    try {
      const req = CompressConfig.create({
        source: FileObject.create({ ...source }),
        maxWidth: compressionOptions.maxWidth,
        maxHeight: compressionOptions.maxHeight,
        quality: compressionOptions.quality,
        format: compressionOptions.format,
      });

      return await this.Compress(req);
    } catch (e) {
      console.error('Error in compressImage:', e);
      return null;
    }
  };

  // Helper method to upload an image with compression in one operation
  public uploadCompressedImage = async (
    fileData: string,
    fileName: string,
    bucketName: string,
    compressionOptions: CompressionOptions,
    tagString?: string
  ) => {
    try {
      // First upload the original file
      const uploadResult = await this.uploadFileToS3Bucket(
        fileName,
        fileData,
        bucketName,
        tagString
      );

      if (uploadResult !== 'ok') {
        console.error('Failed to upload original file');
        return 'nok';
      }

      // Create source file reference
      const source: SimpleFile = {
        bucket: bucketName,
        key: fileName
      };

      // Generate a name for the compressed file
      const fileNameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
      const compressedFileName = `${fileNameWithoutExt}_compressed.${compressionOptions.format.toLowerCase()}`;
      
      // Create destination file reference
      const destination: SimpleFile = {
        bucket: bucketName,
        key: compressedFileName
      };

      // Compress and move the file
      const result = await this.compressAndMoveFile(
        source,
        destination,
        compressionOptions,
        true // preserve the source file
      );

      return result;
    } catch (e) {
      console.error('Error in uploadCompressedImage:', e);
      return 'nok';
    }
  };

  // Helper method to upload and compress an image in one call
  // This method: 1) uploads the raw data to S3, 2) calls CompressAndUpload on the uploaded file
  // Note: The backend's CompressAndUpload fetches from S3, so we must upload first
  public compressAndUploadImage = async (
    data: Uint8Array,
    fileName: string,
    bucketName: string,
    compressionOptions: CompressionOptions
  ) => {
    try {
      // Step 1: Convert Uint8Array to base64 data URL for initial upload
      let binaryString = '';
      for (let i = 0; i < data.length; i++) {
        binaryString += String.fromCharCode(data[i]);
      }
      const base64String = btoa(binaryString);
      
      // Determine MIME type from file extension
      const mimeType = getMimeType(fileName) || 'image/jpeg';
      const dataUrl = `data:${mimeType};base64,${base64String}`;

      // Step 2: Upload original file to S3
      const uploadResult = await this.uploadFileToS3Bucket(
        fileName,
        dataUrl,
        bucketName
      );

      if (uploadResult !== 'ok') {
        console.error('Failed to upload original file for compression');
        return 'nok';
      }

      // Step 3: Now compress the uploaded file (backend will fetch it from S3)
      const sourceFile = FileObject.create({
        bucket: bucketName,
        key: fileName,
      });

      const req = CompressConfig.create({
        source: sourceFile,
        maxWidth: compressionOptions.maxWidth,
        maxHeight: compressionOptions.maxHeight,
        quality: compressionOptions.quality,
        format: compressionOptions.format,
      });

      const result = await this.CompressAndUpload(req);
      return result ? 'ok' : 'nok';
    } catch (e) {
      console.error('Error in compressAndUploadImage:', e);
      return 'nok';
    }
  };

  // Multipart upload methods
  public async startMultipartUpload(req: MultipartUpload) {
    let res = MultipartData.create();
    try {
      res = await this.self.startMultipartUpload(req, this.getMetaData()).response;
    } catch (err) {
      console.error('❌ Error starting multipart upload:', {
        error: err,
        bucket: req.finalBucket,
        key: req.key,
        mimeType: req.mimeType
      });
      throw err;
    }
    return res;
  }

  public async getMultipartUploadUrl(req: MultipartData) {
    let res = MultipartData.create();
    try {
      res = await this.self.getMultipartUploadUrl(req, this.getMetaData()).response;
    } catch (err) {
      console.error('❌ Error getting multipart upload URL:', {
        error: err,
        uploadId: req.uploadId,
        key: req.key,
        partNumber: req.partNumber
      });
      throw err;
    }
    return res;
  }

  public async endMultipartUpload(req: MultipartUpload) {
    let res = ProtoFile.create();
    try {
      res = await this.self.endMultipartUpload(req, this.getMetaData()).response;
    } catch (err) {
      console.error('❌ Error ending multipart upload:', {
        error: err,
        uploadId: req.uploadId,
        bucket: req.finalBucket,
        key: req.key,
        partsCount: req.parts.length
      });
      throw err;
    }
    return res;
  }

  // Utility method to upload large files in chunks
  public async uploadLargeFile({
    file,
    bucket,
    key,
    chunkSize,
    onProgress
  }: {
    file: File;
    bucket: string;
    key: string;
    chunkSize?: number;
    onProgress?: (progress: number) => void;
  }) {
    // S3 requires every part except the last to be at least 5MiB; the last part may be any size.
    const minChunkSize = 5 * 1024 * 1024;
    const finalChunkSize = Math.max(chunkSize ?? 10 * 1024 * 1024, minChunkSize);
    const totalChunks = Math.ceil(file.size / finalChunkSize);
    const lastChunkSize = file.size % finalChunkSize;
    
    const uploadContext = {
      fileName: file.name,
      fileSize: file.size,
      bucket,
      key,
      originalChunkSize: chunkSize ?? 'auto',
      finalChunkSize,
      totalChunks,
      lastChunkSize: lastChunkSize || finalChunkSize
    };


    let uploadId = '';
    const chunks: ArrayBuffer[] = [];

    try {
      // Split file into chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * finalChunkSize;
        const end = Math.min(start + finalChunkSize, file.size);
        const chunk = file.slice(start, end);
        const chunkBuffer = await chunk.arrayBuffer();
        chunks.push(chunkBuffer);
      }

      // Start multipart upload
      const multipartUpload = MultipartUpload.create({
        uploadId: '',
        finalBucket: bucket,
        key,
        mimeType: file.type,
        parts: []
      });

      const startResponse = await this.startMultipartUpload(multipartUpload);
      uploadId = startResponse.uploadId;
      // Removed verbose console logs for production readiness

      // Upload each chunk – PARALLEL with retry logic (replaces previous sequential loop)
      const parts: MultipartDetails[] = [];
      const maxRetries = 3;
      let completedParts = 0;

      // Convenience delay for exponential back-off between retries
      const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

      const uploadPart = async (
        partNumber: number,
        attempt = 1
      ): Promise<void> => {
        const chunk = chunks[partNumber - 1];
        const chunkStart = (partNumber - 1) * finalChunkSize;

        try {
          // Get signed URL for this part
          const partData = MultipartData.create({
            uploadId,
            uniqueUrl: '',
            key,
            partNumber
          });
          const urlResponse = await this.getMultipartUploadUrl(partData);

          // Upload the chunk via signed URL
          const uploadHeaders = {
            'Content-Type': 'application/json'
          } as Record<string, string>;

          const uploadResponse = await fetch(urlResponse.uniqueUrl, {
            method: 'PUT',
            body: chunk,
            headers: uploadHeaders
          });

          if (!uploadResponse.ok) {
            throw new Error(`Upload failed with status ${uploadResponse.status}`);
          }

          // Success – extract ETag
          const eTag = uploadResponse.headers.get('ETag')?.replace(/"/g, '') || '';
          parts.push(
            MultipartDetails.create({
              eTag,
              part: partNumber
            })
          );

          completedParts += 1;
          if (onProgress) {
            const progressPercent = (completedParts / totalChunks) * 100;
            onProgress(progressPercent);
          }

          // Removed verbose console logs for production readiness
        } catch (error) {
          console.error(`❌ Error uploading part ${partNumber} (attempt ${attempt})`, error);
          if (attempt < maxRetries) {
            // simple exponential back-off (500ms * attempt)
            await delay(500 * attempt);
            return uploadPart(partNumber, attempt + 1);
          }
          throw error; // Bubble up after exhausting retries
        }
      };

      // Kick off uploads in parallel
      await Promise.all(chunks.map((_, idx) => uploadPart(idx + 1)));

      // Ensure parts are sorted by part number before completing upload
      parts.sort((a, b) => a.part - b.part);

      // Complete multipart upload
      // Removed verbose console logs for production readiness
      const completeRequest = MultipartUpload.create({
        uploadId,
        finalBucket: bucket,
        key,
        mimeType: file.type,
        parts
      });

      // Summary of what we're sending to complete the upload
      // Removed verbose console logs for production readiness

      const result = await this.endMultipartUpload(completeRequest);
      // Removed verbose console logs for production readiness

      return result;

    } catch (error) {
      const errorContext = {
        error,
        uploadContext,
        uploadId,
        totalChunks,
        completedChunks: chunks.length
      };
      
      console.error('💥 Multipart upload failed:', errorContext);



      // Re-throw with enhanced context
      const enhancedError = new Error(`Multipart upload failed for ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      (enhancedError as any).originalError = error;
      (enhancedError as any).uploadContext = errorContext;
      
      throw enhancedError;
    }
  }

  /**
   * Downloads multiple files as a zip archive
   * @param req - FileIdList containing file IDs to download
   * @param filename - optional name for the downloaded zip file
   * @returns Promise that resolves when download is complete
   */
  public async DownloadZip(req: FileIdList, filename = 'photos.zip'): Promise<void> {
    try {
      const call = this.self.getZipFiles(req, this.getMetaData());
      const chunks: Uint8Array[] = [];

      // Collect all chunks from the stream
      for await (const chunk of call.responses) {
        if (chunk.data) {
          chunks.push(chunk.data);
        }
      }

      // Combine all chunks into a single Uint8Array
      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const combined = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.length;
      }

      // Create a blob and trigger download
      const blob = new Blob([combined], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading zip:', err);
      throw err;
    }
  }
}

enum ACL {
  Private = 0,
  PublicRead = 1,
  PublicReadWrite = 2,
}

interface SimpleFile {
  bucket: string;
  key: string;
}

const CLASSIFICATION = 'Classification';
const SUBJECT = 'Subject';
const RECEIPT_TAG = `${CLASSIFICATION}=Receipt`;
const EDUCATION_TAG = `${CLASSIFICATION}=Education`;
const WORKMANSHIP_TAG = `${CLASSIFICATION}=Workmanship`;
const MAKE_SUBJECT_TAG = (s: string) => `${SUBJECT}=${s}`;
const MAKE_CLASSIFICATION_TAG = (c: string) => `${CLASSIFICATION}=${c}`;
const MAKE_TAG = (key: string, value: string) => `${key}=${value}`;

const CLASSIFICATION_TAGS = [
  {
    label: 'Receipts',
    value: RECEIPT_TAG,
  },
  {
    label: 'Education',
    value: EDUCATION_TAG,
  },
  {
    label: 'Workmanship',
    value: WORKMANSHIP_TAG,
  },
];

const SUBJECT_TAGS_TRANSACTIONS = [
  {
    label: 'Receipt',
    value: MAKE_SUBJECT_TAG('Receipt'),
  },
  {
    label: 'Pick Ticket',
    value: MAKE_SUBJECT_TAG('PickTicket'),
  },
  {
    label: 'Invoice',
    value: MAKE_SUBJECT_TAG('Invoice'),
  },
];

const SUBJECT_TAGS_ACCOUNTS_PAYABLE = [
  {
    label: 'Select tag',
    value: '0'
  },
  ...SUBJECT_TAGS_TRANSACTIONS,
];

const SUBJECT_TAGS = [
  ...SUBJECT_TAGS_ACCOUNTS_PAYABLE,
  {
    label: 'Data Tag',
    value: MAKE_SUBJECT_TAG('DataTag'),
  },
  {
    label: 'Refrigerant Report',
    value: MAKE_SUBJECT_TAG('RefrigerantReport'),
  },
];

const CHANNELS = [
  {
    label: '#repair-quotes',
    value: 'C0HMJ00P2',
  },
  {
    label: '#sales-calls',
    value: 'C0HEASA06',
  },
];

export {
  FileObject,
  URLObject,
  S3Client,
  ACL,
  SUBJECT_TAGS,
  SUBJECT_TAGS_TRANSACTIONS,
  SUBJECT_TAGS_ACCOUNTS_PAYABLE,
  MultipartData, MultipartDetails, MultipartUpload,
  InternalResource,
  InternalResourceList,
  CompressConfig,
  CompressMoveConfig,
  FileIdList,
  FileChunk,
};

type LoadInternalResource = {
  page: number;
  filter: InternalResourceFilter;
  sort: InternalResourceSort;
};

type InternalResourceFilter = {
  tag?: number;
  name?: string;
  description?: string;
};

type InternalResourceSort = {
  orderBy: string;
  orderDir: 'ASC' | 'DESC';
};

export type { LoadInternalResource, InternalResourceFilter, InternalResourceSort };

// Type for image compression options
export type CompressionOptions = {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  format: string;
};
